import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppService } from '../_shared/services/app.service';
import { HttpService } from '../_shared/services/http.service';
import { UrlService } from '../_shared/services/url.service';
import { QuoteStatus } from '../_shared/models/quote.model';
import { SchemePdf } from '../_shared/components/documents-selection/documents-selection.component';

export interface SearchParams {
    salesArea: string;
    partnerId?: string;
    page?: string;
    pageSize: string;
    sortBy: string;
    sortOrder: string;
    search?: string;
    fromDate?: Date;
    toDate?: Date;
    customerPurchaseNumber?: string;
    submissionNumber?: string;
    offerNumber?: string;
}

export interface UseTemplateParams {
    id: string;
    customerPurchaseNumber: string;
}

interface BaseOfferParams {
    customerPurchaseNumber: string;
}

export interface CreateOfferParams extends BaseOfferParams {
    model: string;
}

export interface CopyOfferParams extends BaseOfferParams {
    id: string;
}

export interface DeleteOfferParams {
    id: string;
    status: string;
    reason: string;
}

export interface QuoteItem {
    id: string;
    quickRef: string;
    quoteId: string;
    status: QuoteStatus;
    customerPurchaseNumber: string;
    customerNumber: string;
    customerName: string;
    submissionNumber: string;
}

export interface QuoteEditItem extends QuoteItem {
    description?: string;
    scheme?: SchemePdf;
    subType?: string;
}

interface SearchResult<T> {
    totalCount: number;
    items: T[];
}

@Injectable({
    providedIn: 'root',
})
export class OverviewDataService {
    private readonly baseUrl = `${environment.http.quotation}quotations/quote-assist-pro`;
    constructor(private urlService: UrlService, private httpService: HttpService, private appService: AppService) {}

    public copyOffer({ id, customerPurchaseNumber }: CopyOfferParams): Observable<{ id: string }> {
        const url = `${this.baseUrl}/quotes/${id}/copy`;
        return this.httpService.post<{ id: string }>(url, { customerPurchaseNumber: customerPurchaseNumber });
    }

    public createNewOffer(params: CreateOfferParams): Observable<{ id: string }> {
        const url = `${this.baseUrl}/quotes`;
        return this.httpService.post<{ id: string }>(url, {
            ...params,
            language: this.appService.getLanguage(),
            salesOrg: this.appService.getSalesOrg(),
        });
    }

    public deleteOffer(params: DeleteOfferParams): Observable<any> {
        const queryParams = params.status.toLowerCase() !== 'new' ? { reason: params.reason } : undefined;
        return this.httpService.delete(`${this.baseUrl}/quotes/${params.id}`, queryParams);
    }

    public getAvailableCancellationReasons() {
        const url = this.urlService.getUrl('quote.cancelKeys');
        return this.httpService.get(url);
    }

    public useTemplate({ id, customerPurchaseNumber }: UseTemplateParams): Observable<{ id: string }> {
        const url = `${this.baseUrl}/templates/${id}/quotes`;
        return this.httpService.post(url, { customerPurchaseNumber });
    }

    public searchOffers(parameters: SearchParams) {
        const url = `${this.baseUrl}/quotes`;
        const params: HttpParams = new HttpParams({
            fromObject: this.removeUndefined({
                page: parameters.page?.toString(),
                perPage: parameters.pageSize,
                sortBy: parameters.sortBy,
                sortOrder: parameters.sortOrder,
                salesOrg: this.appService.getSalesOrg(),
                partnerId: parameters.partnerId?.trim(),
                createdAfter: parameters.fromDate?.toISOString(),
                createdBefore: parameters.toDate?.toISOString(),
                customerPurchaseNumber: parameters.customerPurchaseNumber?.trim(),
                submissionNumber: parameters.submissionNumber?.trim(),
                offerNumber: parameters.offerNumber?.trim(),
            }),
        });
        return this.httpService.get<SearchResult<QuoteItem>>(url, params);
    }

    public searchTemplates(parameters: SearchParams) {
        const url = `${this.baseUrl}/templates`;

        const params: HttpParams = new HttpParams({
            fromObject: this.removeUndefined({
                page: parameters.page?.toString(),
                perPage: parameters.pageSize,
                sortBy: parameters.sortBy,
                sortOrder: parameters.sortOrder,
                salesOrg: this.appService.getSalesOrg(),
                createdAfter: parameters.fromDate?.toISOString(),
                createdBefore: parameters.toDate?.toISOString(),
                keyword: parameters.search?.trim(),
            }),
        });
        return this.httpService.get(url, params);
    }

    private removeUndefined(obj: { [key: string]: string }) {
        return Object.keys(obj).reduce((prev, key) => (obj[key] ? { ...prev, [key]: obj[key] } : prev), {});
    }
}
